import { useState } from 'react';
import { useParams } from 'react-router';
import { useSuspenseHostQuery } from '#api/hosts.queries.ts';
import { BlockIcon } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageCloser } from '#components/patterns/page-closer/PageCloser.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useReturnNavigation } from '#hooks/useAppLocation.ts';
import { useEnrollmentState } from '#hooks/useEnrollmentState.ts';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { hostsListTo } from '#screens/hosts/listLocation.ts';
import { page, Titled } from '#util/title.ts';
import { UnblockHostDialog } from '../unblock/UnblockHostDialog.tsx';
import { ArchitectureTabs } from './architecture-tabs/ArchitectureTabs.tsx';

export function EnrollPage() {
  const { hostId } = useParams() as { hostId: string };
  const host = useSuspenseHostQuery(hostId).data.data;
  const [isUnblocking, setIsUnblocking] = useState<boolean>(false);
  const persistedNetworkFilter = usePersistedNetworkFilter();
  const hostNoun =
    host.isLighthouse ? 'lighthouse'
    : host.isRelay ? 'relay'
    : 'host';
  const returnTo = useReturnNavigation(hostsListTo(hostNoun, persistedNetworkFilter));

  function returnToList() {
    returnTo({ replace: false });
  }

  // Do not try to get an enrollment token if the host is blocked or oidc device
  const enrollmentState = useEnrollmentState(hostId, {
    onContinue: returnToList,
    enabled: !host.isBlocked && !host.endpointOIDCUserID,
  });

  return (
    // The route-level Titled only knows "host" — the kind lives on the fetched record, so the
    // kind-aware tab title ("Enroll lighthouse") has to be set here, once the host has loaded.
    <Titled title={() => page(`Enroll ${hostNoun}`)}>
      <MainLayout
        header={
          <PageHeader
            title={
              <span>
                Set up <span>{host.name}</span>
              </span>
            }
          >
            <PageCloser fallbackTo={`/hosts/${host.id}`} />
          </PageHeader>
        }
      >
        {host.isBlocked ?
          <Banner className="mb-24" Icon={BlockIcon} variant="alert">
            This {hostNoun} is blocked and can’t communicate with other hosts.{' '}
            <InlineButton
              onClick={() => {
                setIsUnblocking(true);
              }}
            >
              Unblock
            </InlineButton>{' '}
            before re-enrolling it.
          </Banner>
        : host.endpointOIDCUserID ?
          <Banner className="mb-24" Icon={BlockIcon} variant="alert">
            This host is an OIDC device and cannot be enrolled using an enrollment code.
          </Banner>
        : <>
            <h2 className="h3">1. Select OS</h2>
            <ArchitectureTabs hostNoun={hostNoun} enrollmentState={enrollmentState} />
          </>
        }
        {isUnblocking && (
          <UnblockHostDialog
            hostId={hostId}
            onClose={() => {
              setIsUnblocking(false);
            }}
          />
        )}
      </MainLayout>
    </Titled>
  );
}
